import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { PhoneField } from '../form/PhoneField';
import { inputBase, inputBorder } from '../form/fieldStyles';
import { CvUpload } from './CvUpload';
import { heardAboutOptions } from '../../data/careers';
type Values = {
  name: string;
  email: string;
  phone: string;
  linkedin: string;
  note: string;
  heard: string;
  consent: boolean;
};
type FieldKey = 'name' | 'email' | 'phone' | 'linkedin' | 'cv' | 'heard' | 'consent';
type Errors = Partial<Record<FieldKey, string>>;
const labelCls = 'mb-1.5 block text-[14px] font-medium text-ink';
const errCls = 'mt-1.5 text-[13px] font-medium text-[#C0362C]';
export function ApplicationForm({
  jobTitle
}: {
  jobTitle: string;
}) {
  const reduce = useReducedMotion();
  const [values, setValues] = useState<Values>({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    note: '',
    heard: '',
    consent: false
  });
  const [country, setCountry] = useState('RW');
  const [cv, setCv] = useState<File | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const set = <K extends keyof Values,>(key: K, value: Values[K]) => {
    setValues(v => ({
      ...v,
      [key]: value
    }));
    if (errors[key as FieldKey]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const validate = (): Errors => {
    const e: Errors = {};
    if (values.name.trim().length < 2) e.name = 'Enter your full name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) e.email = 'Enter a valid email address';
    const digits = values.phone.replace(/\D/g, '');
    if (country === 'RW' ? !/^0?7\d{8}$/.test(digits) : digits.length < 7) e.phone = 'Enter a valid phone number';
    if (values.linkedin.trim() && !/linkedin\.com\//i.test(values.linkedin)) e.linkedin = 'Enter a LinkedIn profile URL';
    if (!cv && !errors.cv) e.cv = 'Upload your CV';else if (errors.cv) e.cv = errors.cv;
    if (!values.heard) e.heard = 'Choose an option';
    if (!values.consent) e.consent = 'Please agree so we can review your application';
    return e;
  };
  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    const first = Object.keys(e)[0];
    if (first) {
      document.getElementById(`apply-${first}`)?.focus();
      return;
    }
    setStatus('loading');
    window.setTimeout(() => setStatus('success'), 1100);
  };
  if (status === 'success') {
    return <motion.div initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} role="status" className="rounded-[16px] border border-canvas-line bg-white px-6 py-12 text-center sm:px-10" data-id="element-179" data-mp="1wku9k8vgxby2">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E6F4EA] text-[#1B7A3D]" data-id="element-180" data-mp="3myuqgz4pn5mc">
          <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-181" data-mp="18pifrosmunjt" />
        </span>
        <h3 className="display mt-5 text-[28px] font-semibold text-ink" data-id="element-182" data-mp="1dntrk52hn84o">Application received</h3>
        <p className="mx-auto mt-3 max-w-[420px] text-[16px] leading-[1.55] text-ink-soft" data-id="element-183" data-mp="1igmuqoib68en">
          Thanks for applying for {jobTitle}. We’ll review it and get back to you within [X] days.
        </p>
        <SmartLink href="/careers" className="mt-7 inline-flex h-12 items-center rounded-[7px] border border-ink/20 px-6 text-[15px] font-semibold text-ink transition-colors duration-150 ease-out hover:border-ink/45" data-id="element-184" data-mp="ps7sl2k9zkkq">
          See other roles
        </SmartLink>
      </motion.div>;
  }
  return <form onSubmit={submit} noValidate className="rounded-[16px] border border-canvas-line bg-white p-6 sm:p-8" data-id="element-185" data-mp="13okx7b3pnrhs">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-186" data-mp="2a944vmi2ia3h">
        <Field id="apply-name" label="Full name" required error={errors.name} data-id="element-187" data-mp="2f49ky4osgujn">
          <input id="apply-name" autoComplete="name" placeholder="e.g. Aline Uwimana" value={values.name} onChange={e => set('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'apply-name-err' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.name))}`} data-id="element-188" data-mp="2dpqfeuvhjnlu" />
        </Field>
        <Field id="apply-email" label="Email" required error={errors.email} data-id="element-189" data-mp="2wlgi6kb8so0s">
          <input id="apply-email" type="email" inputMode="email" autoComplete="email" placeholder="aline@example.rw" value={values.email} onChange={e => set('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'apply-email-err' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.email))}`} data-id="element-190" data-mp="2rnx5hou70jay" />
        </Field>
        <Field id="apply-phone" label="Phone number" required error={errors.phone} data-id="element-191" data-mp="3u2165g4hj7r0">
          <PhoneField id="apply-phone" value={values.phone} country={country} hasError={Boolean(errors.phone)} required describedBy={errors.phone ? 'apply-phone-err' : undefined} onChange={v => set('phone', v)} onCountryChange={setCountry} onBlur={() => undefined} data-id="element-192" data-mp="2tdopb9d84oq" />
        </Field>
        <Field id="apply-linkedin" label="LinkedIn URL" optional error={errors.linkedin} data-id="element-193" data-mp="3g20622w1o4cl">
          <input id="apply-linkedin" type="url" inputMode="url" placeholder="linkedin.com/in/your-name" value={values.linkedin} onChange={e => set('linkedin', e.target.value)} aria-invalid={Boolean(errors.linkedin)} aria-describedby={errors.linkedin ? 'apply-linkedin-err' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.linkedin))}`} data-id="element-194" data-mp="31d3rdmzhft30" />
        </Field>
      </div>

      <div className="mt-5" data-id="element-195" data-mp="y2h7fc2zf9ta">
        <p className={labelCls} data-id="element-196" data-mp="y27ilrtob8p6">
          CV <span className="text-brand" data-id="element-197" data-mp="t45yr4ofb5cn">*</span>
        </p>
        <CvUpload file={cv} error={errors.cv} onChange={(f, err) => {
        setCv(f);
        setErrors(e => ({
          ...e,
          cv: err
        }));
      }} data-id="element-198" data-mp="t4p6jskd0dcy" />
        {errors.cv && <p className={errCls} data-id="element-199" data-mp="1lr0gvuxrpsmy">{errors.cv}</p>}
      </div>

      <div className="mt-5" data-id="element-200" data-mp="1gv30jomi8ma6">
        <Field id="apply-note" label="Cover note" optional data-id="element-201" data-mp="1gut8lvhwg51u">
          <textarea id="apply-note" rows={4} placeholder="Tell us why you’d like to join Kayko and what you’d bring." value={values.note} onChange={e => set('note', e.target.value)} className={`${inputBase} ${inputBorder(false)} h-auto py-3 leading-[1.5]`} data-id="element-202" data-mp="1crn7115qf21p" />
        </Field>
      </div>

      <div className="mt-5 sm:max-w-[calc(50%-10px)]" data-id="element-203" data-mp="281id5piskylg">
        <Field id="apply-heard" label="How did you hear about us?" required error={errors.heard} data-id="element-204" data-mp="28336xcxa8di9">
          <div className="relative" data-id="element-205" data-mp="2d14oftf284rm">
            <select id="apply-heard" value={values.heard} onChange={e => set('heard', e.target.value)} aria-invalid={Boolean(errors.heard)} aria-describedby={errors.heard ? 'apply-heard-err' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.heard))} appearance-none pr-10 ${values.heard ? '' : 'text-ink-muted/70'}`} data-id="element-206" data-mp="2h284afx3y0su">
              <option value="" disabled data-id="element-207" data-mp="30pxpg4ajh2t3">
                Choose one
              </option>
              {heardAboutOptions.map(o => <option key={o} value={o} data-id="element-208" data-mp="34qznwiwmtau0">
                  {o}
                </option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-4 h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-209" data-mp="1adrr4lp3ip5q" />
          </div>
        </Field>
      </div>

      <div className="mt-6" data-id="element-210" data-mp="rfolqjy05116">
        <label className="flex cursor-pointer items-start gap-3" data-id="element-211" data-mp="mifyw61ws8tl">
          <input id="apply-consent" type="checkbox" checked={values.consent} onChange={e => set('consent', e.target.checked)} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'apply-consent-err' : undefined} className="mt-0.5 h-5 w-5 shrink-0 rounded border-ink/30 accent-[#1C8CDE]" data-id="element-212" data-mp="mi68wigv61nw" />
          <span className="text-[14px] leading-[1.55] text-ink-soft" data-id="element-213" data-mp="3ln6sd4h9e0u">
            I agree that Kayko may store and use my details to assess my application, as described in the{' '}
            <SmartLink href="/legal/privacy" className="font-medium text-ink underline underline-offset-2 hover:text-brand" data-id="element-214" data-mp="cip5k7p33zv3">
              Privacy Policy
            </SmartLink>
            .
          </span>
        </label>
        {errors.consent && <p id="apply-consent-err" className={errCls} data-id="element-215" data-mp="1wfwjfkhanr0k">
            {errors.consent}
          </p>}
      </div>

      <button type="submit" disabled={status === 'loading'} className="mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[7px] bg-brand px-6 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover disabled:opacity-80 sm:w-auto" data-id="element-216" data-mp="20j2m6i57702a">
        {status === 'loading' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-217" data-mp="2k5qupxlyt7ip" />
            Submitting…
          </> : 'Submit application'}
      </button>
    </form>;
}
function Field({
  id,
  label,
  required,
  optional,
  error,
  children
}: {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-218" data-mp="32wrqydvpaoyk">
      <label htmlFor={id} className={labelCls} data-id="element-219" data-mp="32xjmxpt7mopd">
        {label} {required && <span className="text-brand" data-id="element-220" data-mp="12puwh70p58b0">*</span>}
        {optional && <span className="font-normal text-ink-muted" data-id="element-221" data-mp="12qecgi6sdmme">(optional)</span>}
      </label>
      {children}
      {error && <p id={`${id}-err`} className={errCls} data-id="element-222" data-mp="17mbuqu1m131t">
          {error}
        </p>}
    </div>;
}